import React, { useState, useEffect } from "react";
import Base from "../Employee_components/Employee_Base";
import { Breadcrumbs, Link } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import "../Employee Section/AddEmployees.css";
import { EMPLOYEE_API_BASE_URL } from "../Auth/Api";
import UseAxios from "../Auth/UseAxios";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../Auth/AuthContext";
import Swal from "sweetalert2";

const EditEmployees = () => {
  const api = UseAxios();
  const { id } = useParams();
  const navigate = useNavigate();
  const { employee } = useAuth();

  const [formData, setFormData] = useState({
    fullName: "",
    dateOfBirth: "",
    email: "",
    mobile: "",
    location: "",
    designation: "",
    address: "",
    documentName: "",
    managerReferenceId: "",
    existingDocumentFile: "",
  });

  const [documentFile, setDocumentFile] = useState(null);
  const [managers, setManagers] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Fetch employee data
  useEffect(() => {
    const fetchEmployee = async () => {
      try {
        const response = await api.get(
          `${EMPLOYEE_API_BASE_URL}/employee/get-employee/${id}`
        );
        const emp = response.data;
        console.log("Fetched employee data:", emp);
        setFormData({
          fullName: emp.fullName || "",
          dateOfBirth: emp.dateOfBirth || "",
          email: emp.email || "",
          mobile: emp.mobile || "",
          location: emp.location || "",
          designation: emp.designation || "",
          address: emp.address || "",
          documentName: emp.documentName || "",
          managerReferenceId: emp.manager?.employeeReferenceId || "",
          existingDocumentFile: emp.documentFile || "",
        });
      } catch (error) {
        console.error("Error fetching employee:", error);
        Swal.fire("Error", "Failed to fetch employee details.", "error");
      }
    };

    fetchEmployee();
  }, [id]);

  // Fetch managers for dropdown
  useEffect(() => {
    const fetchManagers = async () => {
      try {
        const response = await api.get(
          `${EMPLOYEE_API_BASE_URL}/employee/hierarchy/${employee.employeeReferenceId}`
        );
        setManagers(response.data);
      } catch (err) {
        console.error("Error fetching managers:", err);
      }
    };
    fetchManagers();
  }, [employee.employeeReferenceId]);

  // Handle input changes
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle document file selection
  const handleFileChange = (e) => {
    setDocumentFile(e.target.files[0]);
  };

  // Form validation
  const validateForm = () => {
    let errors = {};
    if (!formData.fullName.trim()) errors.fullName = "Full name is required";
    if (!formData.dateOfBirth) errors.dateOfBirth = "Date of birth is required";
    if (!formData.email) errors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      errors.email = "Invalid email format";
    if (!formData.mobile) errors.mobile = "Phone number is required";
    else if (!/^\d{10}$/.test(formData.mobile))
      errors.mobile = "Invalid phone number (10 digits required)";
    if (!formData.location.trim()) errors.location = "Location is required";
    if (!formData.designation) errors.designation = "Designation is required";
    if (!formData.address.trim()) errors.address = "Address is required";
    if (!formData.documentName.trim())
      errors.documentName = "Document name is required";

    return errors;
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setLoading(true);

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setLoading(false);
      return;
    }

    const data = new FormData();
    data.append(
      "employee",
      new Blob([JSON.stringify(formData)], { type: "application/json" })
    );

    // Only append new document if selected
    if (documentFile) {
      data.append("documentFile", documentFile);
    }

    try {
      const response = await api.put(
        `${EMPLOYEE_API_BASE_URL}/employee/edit-employee/${id}`,
        data,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      Swal.fire("Success", "Employee updated successfully!", "success").then(
        () => navigate("/employee/employee_list")
      );
    } catch (error) {
      console.error("Error updating employee:", error);
      Swal.fire(
        "Error",
        error.response?.data || "Failed to update employee.",
        "error"
      );
    }

    setLoading(false);
  };

  return (
    <Base>
      {/* Breadcrumb */}
      <div
        className="pt-3 mt-5"
        style={{
          display: "flex",
          justifyContent: "flex-end",
          paddingRight: "20px",
        }}
      >
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
          <Link
            color="inherit"
            href="/employee/dashboard"
            sx={{ color: "blue", fontWeight: "bold" }}
          >
            Dashboard
          </Link>
          <Link
            color="inherit"
            href="/employee/employee_list"
            sx={{ color: "blue", fontWeight: "bold" }}
          >
            Employee List
          </Link>
        </Breadcrumbs>
      </div>

      <h2
        className="text-center fw-bold fs-1"
        style={{ color: "darkslategrey" }}
      >
        Edit Employee
      </h2>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <hr style={{ width: "90%" }} />
      </div>

      <div className="container vh-400 d-flex justify-content-center align-items-center mt-0 ">
        <form
          className="p-4 border rounded shadow-sm bg-light w-100"
          style={{ maxWidth: "900px", marginTop: "10px" }}
          onSubmit={handleSubmit}
        >
          {/* Full Name */}
          <div className="row mb-3">
            <label className="col-sm-3 col-form-label emp-label">
              Full Name:
            </label>
            <div className="col-sm-7">
              <input
                type="text"
                className="form-control"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
              />
              {errors.fullName && (
                <small className="text-danger">{errors.fullName}</small>
              )}
            </div>
          </div>

          {/* Date of Birth */}
          <div className="row mb-3">
            <label className="col-sm-3 col-form-label emp-label">
              Date of Birth:
            </label>
            <div className="col-sm-7">
              <input
                type="date"
                className="form-control"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
              />
              {errors.dateOfBirth && (
                <small className="text-danger">{errors.dateOfBirth}</small>
              )}
            </div>
          </div>

          {/* Mobile */}
          <div className="row mb-3">
            <label className="col-sm-3 col-form-label emp-label">
              Phone Number:
            </label>
            <div className="col-sm-7">
              <input
                type="tel"
                className="form-control"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
              />
              {errors.mobile && (
                <small className="text-danger">{errors.mobile}</small>
              )}
            </div>
          </div>

          {/* Email */}
          <div className="row mb-3">
            <label className="col-sm-3 col-form-label emp-label">Email:</label>
            <div className="col-sm-7">
              <input
                type="email"
                className="form-control"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
              {errors.email && (
                <small className="text-danger">{errors.email}</small>
              )}
            </div>
          </div>

          {/* Location */}
          <div className="row mb-3">
            <label className="col-sm-3 col-form-label emp-label">
              Location:
            </label>
            <div className="col-sm-7">
              <input
                type="text"
                className="form-control"
                name="location"
                value={formData.location}
                onChange={handleChange}
              />
              {errors.location && (
                <small className="text-danger">{errors.location}</small>
              )}
            </div>
          </div>

          {/* Designation */}
          <div className="row mb-3">
            <label className="col-sm-3 col-form-label emp-label">
              Designation:
            </label>
            <div className="col-sm-7">
              <select
                className="form-control"
                name="designation"
                value={formData.designation}
                onChange={handleChange}
              >
                <option value="">Select Designation</option>
                <option value="GM">General Manager [GM]</option>
                <option value="AGM">Assistant GM [AGM]</option>
                <option value="DM">Development Manager [DM]</option>
                <option value="DO">Development Officer [DO]</option>
                <option value="MO">Marketing Officer [MO]</option>
              </select>
              {errors.designation && (
                <small className="text-danger">{errors.designation}</small>
              )}
            </div>
          </div>

          {/* Address */}
          <div className="row mb-3">
            <label className="col-sm-3 col-form-label emp-label">
              Address:
            </label>
            <div className="col-sm-7">
              <input
                type="text"
                className="form-control"
                name="address"
                value={formData.address}
                onChange={handleChange}
              />
              {errors.address && (
                <small className="text-danger">{errors.address}</small>
              )}
            </div>
          </div>

          {/* Document Name */}
          <div className="row mb-3">
            <label className="col-sm-3 col-form-label emp-label">
              Document Name:
            </label>
            <div className="col-sm-7">
              <input
                type="text"
                className="form-control"
                name="documentName"
                value={formData.documentName}
                onChange={handleChange}
              />
              {errors.documentName && (
                <small className="text-danger">{errors.documentName}</small>
              )}
            </div>
          </div>

          {/* Document File */}
          <div className="row mb-3">
            <label className="col-sm-3 col-form-label emp-label">
              Upload Document:
            </label>
            <div className="col-sm-7">
              <input
                type="file"
                className="form-control"
                onChange={handleFileChange}
              />
              <small className="text-muted">
                {documentFile
                  ? documentFile.name
                  : "Current: " + formData.existingDocumentFile}
              </small>
            </div>
          </div>

          {/* Manager Selection */}
          {/* Manager Selection */}
          {/* Manager Selection */}
          <div className="row mb-3">
            <label className="col-sm-3 col-form-label emp-label">
              Assign To:
            </label>
            <div className="col-sm-7">
              <select
                className="form-control"
                name="managerReferenceId"
                value={formData.managerReferenceId} // selected value
                onChange={handleChange}
              >
                <option value="">Select Manager</option>

                {/* Current logged-in employee as option (if not the employee being edited) */}
                {employee.employeeReferenceId !==
                  formData.employeeReferenceId && (
                  <option value={employee.employeeReferenceId}>
                    {employee.fullName} ({employee.designation})
                  </option>
                )}

                {/* Other managers */}
                {managers
                  .filter(
                    (manager) =>
                      manager.employeeReferenceId !==
                      formData.employeeReferenceId
                  ) // exclude self
                  .map((manager) => (
                    <option
                      key={manager.employeeReferenceId}
                      value={manager.employeeReferenceId}
                    >
                      {manager.fullName} ({manager.designation})
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {/* Submit Button */}
          <div className="emp_btn">
            <button type="submit" className="emp-btn" disabled={loading}>
              {loading ? "Updating..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </Base>
  );
};

export default EditEmployees;
